/**
 * Vault > Reviews & approvals (approved mockup 2026-10-23).
 * States the standard in words ("Every document needs 1 approver"), then the
 * optional extra steps (ADR-0019 rules) as sentences. A rule that asks for no more
 * than the standard is explained rather than silently disabled: that greyed-out
 * Save button is what confused Bill on 2026-10-23.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, type AdminTenant, type ApprovalRule, type Member } from "../../lib/api";
import { TYPE_CODES, TYPE_NAMES, CLASSIFICATION_CODES, CLASSIFICATION_LABELS } from "../../../../shared/codes";
import { useStore } from "../../lib/store";
import { byCoOrder } from "../../lib/brand";
import { Loading } from "../../components/bits";
import { PartTag } from "./SettingsLayout";
import { CompanyPicker } from "./CompanyPicker";

const CYCLES = [6, 12, 18, 24, 36];

export function ApprovalsPage() {
  const { p, ws } = useStore();
  const [sp, setSp] = useSearchParams();
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = async () => { try { setTenants((await api.adminTenants()).tenants.sort(byCoOrder)); } catch (e) { setErr((e as Error).message); } };
  useEffect(() => { void load(); }, []);
  if (!p.anyAdmin) return <Navigate to="/settings" replace />;
  if (err) return <div className="note bad">{err}</div>;
  if (!tenants) return <Loading />;
  const codes = tenants.map((t) => t.code);
  const wanted = sp.get("co") ?? (ws !== "ALL" ? ws : "");
  const t = tenants.find((x) => x.code === (codes.includes(wanted) ? wanted : codes[0]));
  if (!t) return null;
  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="vault" />
        <h1 className="title">Reviews <em>&amp;</em> approvals</h1>
        <div className="lede">How a document gets approved before readers see it. Set per company.</div>
      </div></div>
      <CompanyPicker tenants={tenants} code={t.code} onPick={(c) => setSp({ co: c }, { replace: true })} />
      <Standard key={t.code} t={t} onSaved={load} />
      <ExtraSteps key={"r" + t.code} code={t.code} name={t.name} />
    </div>
  );
}

function Standard({ t, onSaved }: { t: AdminTenant; onSaved: () => Promise<void> }) {
  const { toast } = useStore();
  const [members, setMembers] = useState<Member[] | null>(null);
  useEffect(() => { api.members(t.code).then((r) => setMembers(r.members), () => setMembers([])); }, [t.code]);
  const approvers = (members ?? []).filter((m) => m.roles.includes("approver"));
  const names = approvers.map((a) => a.display_name);
  const policy = async (on: boolean) => {
    try { await api.setPolicy(t.code, on); toast(on ? `In ${t.name}, someone other than the author now approves.` : `In ${t.name}, approvers may approve their own work (labeled Self-approved).`); await onSaved(); }
    catch (e) { toast((e as Error).message); }
  };
  const cycle = async (m: number) => {
    try { await api.updateCompany(t.code, { review_months: m }); toast(`${t.name}: next review ${m} months after approval.`); await onSaved(); }
    catch (e) { toast((e as Error).message); }
  };
  const canSecond = t.approvers >= 2;
  return (
    <section className="card set-box">
      <h3>The standard</h3>
      <p className="sub">Applies to every document unless an extra step below says otherwise.</p>
      <div className="std">
        <span className="n">1</span>
        <div>
          <b>Every document needs 1 approver.</b>
          <div className="cap">{members === null ? "…" : names.length ? `Approvers in ${t.name}: ${names.join(", ")}.` : `${t.name} has no approver yet.`} Add approvers in <Link className="doclink" to={`/settings/people?co=${t.code}`}>People &amp; roles</Link>.</div>
        </div>
      </div>
      <div className="set-row">
        <label className="switch" title={!canSecond && !t.require_second_approver ? "Needs two approvers first" : ""}>
          <input type="checkbox" checked={t.require_second_approver} disabled={!canSecond && !t.require_second_approver} onChange={(e) => void policy(e.target.checked)} aria-label="Authors can't approve their own work" />
          <span />
        </label>
        <div>
          <b>Authors can't approve their own work</b>
          <div className="cap">
            {!canSecond && t.require_second_approver ? `Switched on, but it only takes effect once ${t.name} has two approvers. Until then the only approver approves their own documents, labeled Self-approved.`
              : !canSecond ? `Needs at least two approvers. With one, that person approves their own documents, labeled Self-approved with a note.`
              : t.require_second_approver ? "On: someone other than the author must approve."
              : "Off: an approver can approve a document they wrote. It's labeled Self-approved and needs a note."}
          </div>
        </div>
      </div>
      <div className="set-row">
        <span style={{ width: 38, flex: "0 0 auto" }} />
        <div>
          <b>Review cycle</b>
          <div className="cap">After approval, the next review is due in{" "}
            <select className="inp inline-sel" value={t.review_months} onChange={(e) => void cycle(Number(e.target.value))} aria-label="Review cycle">
              {[...new Set([...CYCLES, t.review_months])].sort((a, b) => a - b).map((m) => <option key={m} value={m}>{m} months{m === 12 ? " (default)" : ""}</option>)}
            </select>
          </div>
        </div>
      </div>
    </section>
  );
}

const nameOf = (kind: string, code: string) => kind === "type" ? (TYPE_NAMES[code] ?? code) : (CLASSIFICATION_LABELS[code] ?? code);
function sentence(x: { match_kind: string; match_code: string; review: boolean; approvals: number }) {
  const what = x.match_kind === "type" ? <>a <b>{nameOf("type", x.match_code)}</b> ({x.match_code})</> : <><b>{nameOf("classification", x.match_code)}</b></>;
  const then = x.approvals > 1 ? <><b>{x.approvals} approvers</b>, none of them the author</> : <>the standard 1 approver</>;
  return <>When a document is {what}: {x.review ? <><b>a review first</b>, then </> : null}{then}.</>;
}

function ExtraSteps({ code, name }: { code: string; name: string }) {
  const { toast } = useStore();
  const [data, setData] = useState<{ rules: ApprovalRule[]; approvers: number; reviewers: number } | null>(null);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<"type" | "classification">("type");
  const [match, setMatch] = useState("POL");
  const [review, setReview] = useState(false);
  const [approvals, setApprovals] = useState(2);
  const [hint, setHint] = useState<string | null>(null);
  const load = async () => { try { setData(await api.rules(code)); } catch (e) { toast((e as Error).message); } };
  useEffect(() => { void load(); }, [code]);
  if (!data) return <Loading />;
  const options = kind === "type" ? TYPE_CODES.map((c) => [c, TYPE_NAMES[c] ?? c]) : CLASSIFICATION_CODES.map((c) => [c, CLASSIFICATION_LABELS[c] ?? c]);
  const add = async () => {
    if (!review && approvals === 1) { setHint("That's already the standard: every document needs 1 approver. Ask for a review first or more approvers to add a step."); return; }
    try {
      await api.setRule(code, { match_kind: kind, match_code: match, review, approvals });
      toast(`Extra step saved for ${name}.`); setAdding(false); setHint(null); setReview(false); setApprovals(2); await load();
    } catch (e) { toast((e as Error).message); }
  };
  const remove = async (x: ApprovalRule) => {
    if (!window.confirm(`Remove the extra step for ${nameOf(x.match_kind, x.match_code)}? Documents already waiting keep their route.`)) return;
    try { await api.removeRule(code, x.id); toast("Extra step removed."); await load(); } catch (e) { toast((e as Error).message); }
  };
  return (
    <section className="card set-box">
      <h3>Extra steps for some documents <span className="cap" style={{ fontWeight: 500 }}>optional</span></h3>
      <p className="sub">Ask for more than the standard on certain document types or classifications. {data.rules.length === 0 ? "None yet, so the standard applies to everything." : "They apply to documents submitted after the change."}</p>
      {data.rules.map((x) => (
        <div className="rule-line" key={x.id}>
          <div>{sentence(x)}{x.approvals > data.approvers && <div className="cap warn-t">{name} has only {data.approvers} approver{data.approvers === 1 ? "" : "s"}, so it asks for {data.approvers} until more are added.</div>}
            {x.review && data.reviewers === 0 && <div className="cap warn-t">Nobody has the Reviewer role yet, so an approver does the review.</div>}</div>
          <button className="btn btn-ghost btn-sm" onClick={() => void remove(x)}>Remove</button>
        </div>
      ))}
      {adding ? (
        <div className="sentence">
          <span>When a document's</span>
          <select className="inp" value={kind} aria-label="Match by" onChange={(e) => { const k = e.target.value as "type" | "classification"; setKind(k); setMatch(k === "type" ? "POL" : "REST"); setHint(null); }}>
            <option value="type">type</option><option value="classification">classification</option>
          </select>
          <span>is</span>
          <select className="inp" value={match} aria-label="Which" onChange={(e) => { setMatch(e.target.value); setHint(null); }}>
            {options.map(([c, l]) => <option key={c} value={c}>{l} ({c})</option>)}
          </select>
          <span>, ask for</span>
          <label className="chk"><input type="checkbox" checked={review} onChange={(e) => { setReview(e.target.checked); setHint(null); }} />a review first</label>
          <span>and</span>
          <select className="inp" value={approvals} aria-label="Approvers needed" onChange={(e) => { setApprovals(Number(e.target.value)); setHint(null); }}>
            <option value={1}>1 approver (standard)</option><option value={2}>2 approvers</option><option value={3}>3 approvers</option>
          </select>
          <button className="btn btn-accent btn-sm" onClick={() => void add()}>Add this step</button>
          <button className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setHint(null); }}>Cancel</button>
          {hint && <div className="note info" style={{ flexBasis: "100%" }}>{hint}</div>}
        </div>
      ) : (
        <button className="btn btn-ghost" onClick={() => setAdding(true)}>+ Add an extra step</button>
      )}
      <div className="help">With more than one approver, each is a different person and none is the author. {data.reviewers} can review and {data.approvers} can approve in {name}.</div>
    </section>
  );
}
